import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import PhotoViewer from "@/components/PhotoViewer";
import BrandMark from "@/components/BrandMark";
import { MessageCircle, Trash2, Edit3, Plus, X, CalendarHeart, Share2 } from "lucide-react";
import { useLocale } from "@/lib/locale";
import ShareCardModal from "@/components/ShareCardModal";

export default function PresenceProfile() {
    const { id } = useParams();
    const nav = useNavigate();
    const { t } = useLocale();
    const [p, setP] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({});
    const [starting, setStarting] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    // Auto-open the share modal when arriving via ?share=1 (e.g. from
    // the anniversary email's "Apri il ricordo" link). We strip the
    // query param after reading so reloading the page doesn't keep
    // re-opening the modal.
    useEffect(() => {
        if (searchParams.get("share") === "1" && p?.has_photo) {
            setShareOpen(true);
            const next = new URLSearchParams(searchParams);
            next.delete("share");
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, p?.has_photo, setSearchParams]);

    const load = async () => {
        setLoading(true);
        try {
            const [pRes, cRes] = await Promise.all([
                api.get(`/presences/${id}`),
                api.get(`/presences/${id}/conversations`),
            ]);
            setP(pRes.data);
            setForm(pRes.data);
            setConversations(cRes.data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const startNewConversation = async () => {
        if (starting) return;
        setStarting(true);
        try {
            const { data } = await api.post(`/presences/${id}/open-conversation`);
            nav(`/chat/${data.conversation_id}`, { state: { presence: p, skipOpeningFetch: true } });
        } catch (e) {
            alert(e?.response?.data?.detail || t("profile.not_arrived"));
        } finally {
            setStarting(false);
        }
    };

    const openExisting = (cid) => nav(`/chat/${cid}`, { state: { presence: p } });

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/presences/${id}`, {
                name: form.name,
                relationship: form.relationship,
                address_style: form.address_style,
                personality_traits: form.personality_traits,
                emotional_traits: form.emotional_traits,
                tone_description: form.tone_description,
                typical_phrases: form.typical_phrases,
                never_said: form.never_said,
                anchor_memory: form.anchor_memory,
                farewell_style: form.farewell_style,
                memories: form.memories,
                language: form.language,
            });
            setP(data);
            setForm(data);
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm(t("profile.delete_confirm"))) return;
        await api.delete(`/presences/${id}`);
        nav("/dashboard");
    };

    if (loading || !p) {
        return (
            <div className="grain min-h-screen candle-halo">
                <Header />
                <p className="max-w-4xl mx-auto px-6 pt-20 text-ink-textLow italic font-serif">{t("profile.loading")}</p>
            </div>
        );
    }

    return (
        <div className="grain min-h-screen candle-halo">
            <Header />
            <main className="max-w-4xl mx-auto px-6 md:px-12 pt-14 pb-32 animate-fade-in">
                <Link to="/dashboard" className="btn-ghost text-xs tracking-widest uppercase" data-testid="profile-back">{t("profile.back")}</Link>

                <div className="grid md:grid-cols-[500px_1fr] gap-14 md:gap-24 mt-12 items-start">
                    <div className="photo-frame aspect-[3/4] photo-halo-strong">
                        {p.photo_base64 ? (
                            <PhotoViewer
                                src={`data:${p.photo_mime};base64,${p.photo_base64}`}
                                alt={p.name}
                                className="grayscale-[6%] hover:grayscale-0 transition duration-[900ms]"
                                triggerClassName="w-full h-full"
                                data-testid="presence-photo"
                            />
                        ) : (
                            <div className="w-full h-full bg-ink-surfaceUp flex items-center justify-center">
                                <BrandMark monogram size={180} className="opacity-80" />
                            </div>
                        )}
                    </div>
                    <div>
                        <p className="label-eyebrow mb-4">{p.relationship}</p>
                        <h1 className="h-serif text-5xl md:text-7xl mb-6 leading-[1.05]">{p.name}</h1>
                        {p.voice_transcription && (
                            <div className="card-paper mt-6 mb-10" data-testid="profile-voice">
                                <p className="label-eyebrow mb-3 text-ink-gold">{t("profile.voice_captured")}</p>
                                <p className="font-serif italic text-ink-textHigh text-2xl leading-[1.5]">"{p.voice_transcription}"</p>
                            </div>
                        )}
                        <div className="mt-6" data-testid="profile-actions">
                            <div className="flex flex-wrap gap-3">
                                {conversations.length > 0 ? (
                                    <>
                                        <button
                                            onClick={() => openExisting(conversations[0].conversation_id)}
                                            className="btn-primary inline-flex items-center gap-2"
                                            title={t("profile.resume_d")}
                                            data-testid="profile-resume-chat"
                                        >
                                            <MessageCircle size={16} /> {t("profile.resume")}
                                        </button>
                                        <button
                                            onClick={startNewConversation}
                                            disabled={starting}
                                            className="btn-secondary inline-flex items-center gap-2"
                                            title={t("profile.new_chat_d")}
                                            data-testid="profile-new-chat"
                                        >
                                            <Plus size={16} /> {starting ? t("profile.new_chat_pending") : t("profile.new_chat")}
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={startNewConversation}
                                        disabled={starting}
                                        className="btn-primary inline-flex items-center gap-2"
                                        title={t("profile.open_d")}
                                        data-testid="profile-open-chat"
                                    >
                                        <MessageCircle size={16} /> {starting ? t("profile.new_chat_pending") : t("profile.open")}
                                    </button>
                                )}
                                <button
                                    onClick={() => nav(
                                        p?.has_photo
                                            ? `/presence/${id}/moment`
                                            : `/presence/${id}?edit=photo`
                                    )}
                                    className="btn-secondary inline-flex items-center gap-2"
                                    title={p?.has_photo ? t("profile.create_moment_d") : t("chat.moment_upsell_nophoto")}
                                    data-testid="profile-create-moment"
                                    data-state={p?.has_photo ? "ready" : "needs-photo"}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                                    {p?.has_photo ? t("moment.cta") : t("chat.moment_upsell_add_photo")}
                                </button>
                                <button
                                    onClick={() => setShareOpen(true)}
                                    disabled={!p?.has_photo}
                                    className="btn-secondary inline-flex items-center gap-2 disabled:opacity-50"
                                    title={p?.has_photo ? "Condividi un ricordo" : "Aggiungi prima una foto"}
                                    data-testid="profile-share"
                                >
                                    <Share2 size={16} /> Condividi
                                </button>
                                <button
                                    onClick={() => setEditing((v) => !v)}
                                    className="btn-secondary inline-flex items-center gap-2"
                                    title={t("profile.edit_d")}
                                    data-testid="profile-edit"
                                >
                                    <Edit3 size={16} /> {editing ? t("profile.cancel") : t("profile.edit")}
                                </button>
                                <button
                                    onClick={remove}
                                    className="btn-ghost inline-flex items-center gap-2 text-ink-danger"
                                    title={t("profile.delete_d")}
                                    data-testid="profile-delete"
                                >
                                    <Trash2 size={16} /> {t("profile.delete")}
                                </button>
                            </div>

                            {/* Soft per-action description row — a single line
                                whisper for the primary intent so users know
                                what each button will do before they click.
                                Shown below the row, never in a tooltip alone
                                (tooltips are invisible on touch). */}
                            <p
                                className="mt-4 text-[0.8rem] md:text-sm text-ink-textLow italic font-serif leading-relaxed max-w-xl"
                                data-testid="profile-actions-help"
                            >
                                {t("profile.actions_help")}
                            </p>
                        </div>
                    </div>
                </div>

                {conversations.length > 0 && (
                    <section className="mt-16" data-testid="profile-past-conversations">
                        <p className="label-eyebrow mb-4">{t("profile.past_title")}</p>
                        <div className="divide-y divide-ink-border border-y border-ink-border">
                            {conversations.map((c) => (
                                <button
                                    key={c.conversation_id}
                                    onClick={() => openExisting(c.conversation_id)}
                                    className="w-full text-left py-5 px-2 hover:bg-ink-surface/60 transition-colors duration-300 flex items-start gap-5"
                                    data-testid={`past-conv-${c.conversation_id}`}
                                >
                                    <div className="flex-1">
                                        <p className="font-serif text-ink-textHigh text-[1.3rem] leading-snug">
                                            {c.last_snippet || c.title}
                                        </p>
                                        <p className="text-sm text-ink-textMid mt-3 tracking-widest uppercase">
                                            {c.message_count} {c.message_count === 1 ? t("profile.msg_one") : t("profile.msg_many")}
                                            {" · "}{new Date(c.updated_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <span className="text-ink-textMid self-center">→</span>
                                </button>
                            ))}
                        </div>
                    </section>
                )}

                {p.continuity_log && (
                    <section className="mt-16" data-testid="profile-continuity">
                        <p className="label-eyebrow mb-4">{t("profile.continuity_title")}</p>
                        <div className="card-paper">
                            <p className="font-serif italic text-ink-textMid text-lg leading-relaxed whitespace-pre-wrap">{p.continuity_log}</p>
                        </div>
                    </section>
                )}

                <section className="mt-16 grid md:grid-cols-2 gap-6" data-testid="profile-extras">
                    <UnsolicitedCard presence={p} onChange={(u) => setP({ ...p, ...u })} />
                    <RecurrencesCard presence={p} onChange={(u) => setP({ ...p, ...u })} />
                    <ComingSoonCard title={t("profile.talking_photo_t")} subtitle={t("profile.talking_photo_d")} testId="coming-talking-photo" />
                </section>

                <section className="mt-16 space-y-0">
                    <p className="label-eyebrow mb-4">{t("profile.identity")}</p>
                    {editing ? (
                        <EditFields form={form} setForm={setForm} />
                    ) : (
                        <>
                            <Field label={t("profile.f_address")} value={p.address_style} />
                            <Field label={t("profile.f_personality")} value={p.personality_traits} />
                            <Field label={t("profile.f_emotional")} value={p.emotional_traits} />
                            <Field label={t("profile.f_tone")} value={p.tone_description} />
                            <Field label={t("profile.f_phrases")} value={p.typical_phrases} />
                            <Field label={t("profile.f_anchor")} value={p.anchor_memory} />
                            <Field label={t("profile.f_farewell")} value={p.farewell_style} />
                            <Field label={t("profile.f_never")} value={p.never_said} />
                            <Field label={t("profile.f_memories")} value={p.memories} />
                        </>
                    )}
                </section>

                {editing && (
                    <div className="mt-10 flex gap-3">
                        <button onClick={save} disabled={saving} className="btn-primary" data-testid="profile-save">{saving ? "…" : t("profile.save")}</button>
                        <button onClick={() => { setForm(p); setEditing(false); }} className="btn-ghost">{t("profile.cancel")}</button>
                    </div>
                )}
            </main>
            {shareOpen && (
                <ShareCardModal
                    presenceId={id}
                    presenceName={p?.name}
                    onClose={() => setShareOpen(false)}
                />
            )}
        </div>
    );
}

function Field({ label, value }) {
    const { t } = useLocale();
    return (
        <div className="border-t border-ink-border py-10">
            <p className="label-eyebrow">{label}</p>
            <p className="mt-5 font-serif text-[1.4rem] text-ink-textHigh leading-[1.7] whitespace-pre-wrap">
                {value ? value : <span className="italic text-ink-textLow">{t("profile.f_not_recorded")}</span>}
            </p>
        </div>
    );
}

function UnsolicitedCard({ presence, onChange }) {
    const { t } = useLocale();
    const { presence_id } = presence;
    const [enabled, setEnabled] = useState(presence.unsolicited_enabled !== false);
    const [tone, setTone] = useState(presence.unsolicited_tone || "gentle");
    const [busy, setBusy] = useState(false);

    const save = async (next) => {
        setBusy(true);
        try {
            const { data } = await api.patch(`/presences/${presence_id}/unsolicited-settings`, next);
            onChange?.({
                unsolicited_enabled: data.unsolicited_enabled,
                unsolicited_tone: data.unsolicited_tone,
                last_unsolicited_at: data.last_unsolicited_at,
            });
        } finally {
            setBusy(false);
        }
    };

    const toggle = () => {
        const v = !enabled;
        setEnabled(v);
        save({ unsolicited_enabled: v });
    };
    const changeTone = (k) => {
        setTone(k);
        save({ unsolicited_tone: k });
    };

    return (
        <div className="card-paper" data-testid="unsolicited-card">
            <p className="label-eyebrow mb-2">{t("profile.unsolicited_eyebrow")}</p>
            <h3 className="font-serif text-2xl text-ink-textHigh mb-3">{t("profile.unsolicited_title")}</h3>
            <p className="text-ink-textMid text-base leading-relaxed mb-6">
                {t("profile.unsolicited_body")}
            </p>
            <button
                onClick={toggle}
                disabled={busy}
                data-testid="unsolicited-toggle"
                className={`inline-flex items-center gap-3 px-4 py-2 rounded-sm border transition-colors duration-300 ${
                    enabled ? "border-ink-gold bg-ink-gold/10 text-ink-textHigh" : "border-ink-border text-ink-textMid hover:border-ink-textLow"
                }`}
            >
                <span className={`w-2 h-2 rounded-full ${enabled ? "bg-ink-gold" : "bg-ink-textLow"}`}></span>
                {enabled ? t("profile.unsolicited_on") : t("profile.unsolicited_off")}
            </button>
            {enabled && (
                <div className="mt-6" data-testid="unsolicited-tone-group">
                    <p className="label-eyebrow mb-3">{t("profile.unsolicited_tone")}</p>
                    <div className="flex flex-wrap gap-2">
                        {[
                            { k: "gentle",  label: t("profile.tone_gentle") },
                            { k: "curious", label: t("profile.tone_curious") },
                            { k: "playful", label: t("profile.tone_playful") },
                        ].map((opt) => (
                            <button
                                key={opt.k}
                                onClick={() => changeTone(opt.k)}
                                disabled={busy}
                                data-testid={`unsolicited-tone-${opt.k}`}
                                className={`px-3 py-1.5 rounded-sm text-sm border transition-colors duration-300 ${
                                    tone === opt.k
                                        ? "bg-ink-gold text-ink-deep border-ink-gold"
                                        : "bg-transparent text-ink-textMid border-ink-border hover:border-ink-textLow"
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function ComingSoonCard({ title, subtitle, testId }) {
    const { t } = useLocale();
    return (
        <div className="card-paper relative overflow-hidden opacity-80" data-testid={testId}>
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-ink-gold/5 to-transparent"></div>
            <p className="label-eyebrow mb-2 text-ink-gold">{t("profile.coming_soon")}</p>
            <h3 className="font-serif text-[1.7rem] text-ink-textHigh mb-4">{title}</h3>
            <p className="text-ink-textMid text-lg leading-relaxed">{subtitle}</p>
            <p className="mt-6 text-sm text-ink-textMid italic">{t("profile.plans_only")}</p>
        </div>
    );
}

function EditFields({ form, setForm }) {
    const { t } = useLocale();
    const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const TA = (k, label, rows = 3) => (
        <div className="border-t border-ink-border py-6">
            <label className="label-eyebrow">{label}</label>
            <textarea rows={rows} className="textarea-paper mt-3" value={form[k] || ""} onChange={change(k)} data-testid={`edit-${k}`} />
        </div>
    );
    const IN = (k, label) => (
        <div className="border-t border-ink-border py-6">
            <label className="label-eyebrow">{label}</label>
            <input className="input-line mt-3" value={form[k] || ""} onChange={change(k)} data-testid={`edit-${k}`} />
        </div>
    );
    return (
        <>
            {IN("name", t("profile.f_name"))}
            {IN("relationship", t("profile.f_relationship"))}
            {IN("address_style", t("profile.f_address"))}
            {TA("personality_traits", t("profile.f_personality"), 4)}
            {IN("emotional_traits", t("profile.f_emotional_edit"))}
            {TA("tone_description", t("profile.f_tone"), 4)}
            {TA("typical_phrases", t("profile.f_phrases"), 3)}
            {TA("anchor_memory", t("profile.f_anchor"), 4)}
            {IN("farewell_style", t("profile.f_farewell"))}
            {TA("never_said", t("profile.f_never"), 3)}
            {TA("memories", t("profile.f_memories"), 8)}
        </>
    );
}


// ── RecurrencesCard ──────────────────────────────────────────────────────
// Lets the user attach significant dates to a presence. Kept deliberately
// simple: no year required for annual recurrences (we store MM-DD); when
// the user DOES enter a year, we respect it and can later display
// "years since" on memorial dates.
//
// Design rules this component follows:
//   • Empty state is soft + instructive, never shaming.
//   • Add form is inline (no modal) — one tap to open, two fields to fill.
//   • Remove is explicit (X button) but not destructive red — this is a
//     bereavement product; "red = danger" clashes with the tone.
//   • Optimistic UI: we patch the whole recurrences array with PATCH on
//     save/remove. Conflicts are impossible because a single user edits
//     from a single device at a time.
function RecurrencesCard({ presence, onChange }) {
    const { t } = useLocale();
    const [items, setItems] = useState(presence.recurrences || []);
    const [adding, setAdding] = useState(false);
    const [busy, setBusy] = useState(false);
    const [label, setLabel] = useState("");
    const [kind, setKind] = useState("birthday");
    // Native <input type="date"> gives us YYYY-MM-DD; we strip the year when
    // storing so the date recurs annually regardless of which year was picked.
    const [date, setDate] = useState("");

    const persist = async (next) => {
        setBusy(true);
        try {
            const { data } = await api.patch(`/presences/${presence.presence_id}`, {
                recurrences: next,
            });
            setItems(data.recurrences || []);
            onChange && onChange({ recurrences: data.recurrences || [] });
        } finally {
            setBusy(false);
        }
    };

    const add = async () => {
        if (!label.trim() || !date) return;
        // Keep MM-DD so the anniversary recurs every year. If the user
        // wants a "years since" readout later we can always re-infer
        // birthday age, but the simplest invariant wins at this stage.
        const mmdd = date.length === 10 ? date.slice(5) : date;
        const next = [...items, { label: label.trim(), date: mmdd, kind }];
        await persist(next);
        setAdding(false);
        setLabel("");
        setDate("");
        setKind("birthday");
    };

    const remove = async (index) => {
        const next = items.filter((_, i) => i !== index);
        await persist(next);
    };

    // Pretty-printed "DD/MM" for display regardless of stored precision.
    const fmt = (d) => {
        if (!d) return "";
        const mmdd = d.length === 10 ? d.slice(5) : d;
        const [m, dd] = mmdd.split("-");
        return `${dd}/${m}`;
    };

    return (
        <div className="card-paper" data-testid="recurrences-card">
            <div className="flex items-start gap-3 mb-2">
                <CalendarHeart size={18} className="text-ink-gold mt-1 flex-shrink-0" />
                <div>
                    <p className="label-eyebrow">{t("presence.rec_title")}</p>
                    <p className="text-sm text-ink-textMid mt-1 leading-relaxed max-w-md">
                        {t("presence.rec_sub")}
                    </p>
                </div>
            </div>

            {items.length === 0 && !adding && (
                <p className="text-sm text-ink-textLow italic mt-4 mb-5" data-testid="recurrences-empty">
                    {t("presence.rec_empty")}
                </p>
            )}

            {items.length > 0 && (
                <ul className="mt-5 space-y-2" data-testid="recurrences-list">
                    {items.map((r, i) => (
                        <li
                            key={`${r.date}-${i}`}
                            className="flex items-center justify-between border-l-2 border-ink-gold/50 pl-4 py-2"
                            data-testid={`recurrence-item-${i}`}
                        >
                            <div className="min-w-0">
                                <p className="font-serif text-[1.05rem] text-ink-textHigh">{r.label}</p>
                                <p className="text-xs text-ink-textMid tracking-wide mt-0.5">
                                    {t(`presence.rec_kind_${r.kind || "anniversary"}`)} · {fmt(r.date)}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => remove(i)}
                                disabled={busy}
                                className="text-ink-textLow hover:text-ink-gold p-2 transition-colors disabled:opacity-40"
                                aria-label={t("presence.rec_remove")}
                                data-testid={`recurrence-remove-${i}`}
                            >
                                <X size={16} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {adding ? (
                <div className="mt-5 space-y-3 animate-fade-in" data-testid="recurrence-form">
                    <input
                        className="input-line"
                        placeholder={t("presence.rec_label_ph")}
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        data-testid="recurrence-label-input"
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <select
                            className="input-line bg-ink-deep"
                            value={kind}
                            onChange={(e) => setKind(e.target.value)}
                            data-testid="recurrence-kind-select"
                        >
                            <option value="birthday">{t("presence.rec_kind_birthday")}</option>
                            <option value="anniversary">{t("presence.rec_kind_anniversary")}</option>
                            <option value="memorial">{t("presence.rec_kind_memorial")}</option>
                            <option value="other">{t("presence.rec_kind_other")}</option>
                        </select>
                        <input
                            type="date"
                            className="input-line bg-ink-deep"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            data-testid="recurrence-date-input"
                        />
                    </div>
                    <div className="flex gap-2 pt-2">
                        <button
                            onClick={add}
                            disabled={busy || !label.trim() || !date}
                            className="btn-primary disabled:opacity-50"
                            data-testid="recurrence-save"
                        >
                            {busy ? "…" : t("presence.rec_save")}
                        </button>
                        <button
                            onClick={() => { setAdding(false); setLabel(""); setDate(""); }}
                            className="btn-ghost"
                            data-testid="recurrence-cancel"
                        >
                            {t("presence.rec_cancel")}
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    onClick={() => setAdding(true)}
                    className="btn-secondary mt-5 inline-flex items-center gap-2"
                    data-testid="recurrence-add"
                >
                    <Plus size={14} /> {t("presence.rec_add")}
                </button>
            )}
        </div>
    );
}
